import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import {
  COLUMN_MAX_WIDTH,
  COLUMN_MIN_WIDTH,
  DEFAULT_COLUMN_WIDTHS,
  type ErrorCode,
  type IssueRow,
  type IssueSortField,
  type IssueSummary,
  type SortDirection,
  type SubtaskProgress,
  type TableColumn,
  type TableSettings,
} from '@jisy/shared';
import { ApiRequestError } from '../api/client.js';
import {
  createOptionsQuery,
  useIssueCreate,
  usePlanIssueKeys,
  useSectionColors,
  useUnreadCommentCounts,
} from '../api/queries.js';
import { AssigneeCell, PriorityCell, StatusCell } from './IssueCellEdit.js';
import { formatShortDate, formatUpdated, isOverdue } from '../lib/format.js';
import { ParentPrefix, SectionMark, TypeBadge } from './Badges.js';
import styles from './IssuesTable.module.css';

/**
 * Таблица задач.
 *
 * Строки — не элементы <table>, а grid-строки: колонки должны совпадать по ширине с шапкой,
 * при этом каждая строка остаётся отдельной кнопкой для клавиатуры и скринридера.
 * Роли таблицы проставлены явно, чтобы разметка читалась как таблица.
 *
 * Состав, порядок и ширины колонок задаёт пользователь, поэтому раскладка собирается из
 * настроек в CSS-переменную, а не описана в стилях: правило под каждый набор колонок
 * написать невозможно.
 */

/** Шаг изменения ширины стрелками — ширина настраивается и без мыши. */
const RESIZE_STEP = 16;

/**
 * Минимум названия там, где колонки ужимаются под экран. Без него растягивающаяся колонка
 * отдаёт место фиксированным первой и схлопывается в пару букв — а читают таблицу по названию.
 */
const SUMMARY_MIN_WIDTH = 160;

/** Боковые поля строки — те же `--space-4`, что в стилях; входят в общую ширину таблицы. */
const ROW_PADDING = 32;

/**
 * Узкая колонка без заголовка под отметку «в плане». В настройках вида её нет намеренно:
 * это не поле задачи из Jira, а признак самого Jisy, и прятать или переставлять его незачем.
 * Место у неё сразу за названием — рядом с тем, что читают, а не на краю строки.
 */
const PLAN_COLUMN_WIDTH = 26;

/**
 * Строка в том виде, в каком её рисует таблица. Выдача приходит деревом — задачи и их
 * подзадачи, — а стрелки клавиатуры и счёт строк работают по тому, что видно на экране.
 */
interface VisibleRow {
  issue: IssueSummary;
  /** Строка выдачи; у подзадачи под родителем — null. */
  row: IssueRow | null;
  /** Родитель подзадачи под ним: к нему ведёт стрелка влево. */
  parentKey: string | null;
}

export function IssuesTable({
  issues,
  columns,
  widths,
  onResizeColumn,
  onMoveColumn,
  sort,
  dir,
  onSort,
  selectedKey,
  onSelect,
  onOpen,
  onAddToPlan,
  onLoadMore,
  hasMore,
  loadingMore,
}: {
  /** Строки выдачи; подзадачи, чей родитель тоже в выдаче, лежат в его `children`. */
  issues: IssueRow[];
  /** Видимые колонки в пользовательском порядке. */
  columns: TableColumn[];
  widths: TableSettings['widths'];
  onResizeColumn: (column: TableColumn, width: number) => void;
  /** Перенос колонки на место другой: порядок целиком пересобирает владелец настроек. */
  onMoveColumn: (column: TableColumn, target: TableColumn) => void;
  sort: IssueSortField;
  dir: SortDirection;
  onSort: (field: IssueSortField) => void;
  selectedKey: string | null;
  onSelect: (key: string | null) => void;
  onOpen: (issue: IssueSummary) => void;
  /**
   * Взять задачу в план текущей недели — клавишей P или щелчком по колонке плана.
   * Промис нужен, чтобы отметка появилась сразу, а не после того, как план перечитается.
   */
  onAddToPlan: (issue: IssueSummary) => void | Promise<unknown>;
  onLoadMore: () => void;
  hasMore: boolean;
  loadingMore: boolean;
}): React.ReactElement {
  const { t } = useTranslation();
  const sectionColor = useSectionColors();
  // Задачи своего плана на эту неделю: строка под ними подсвечивается фоном.
  const planKeys = usePlanIssueKeys();
  // Непрочитанные комментарии: тот же список, что в колокольчике, — метка перед названием.
  const unreadComments = useUnreadCommentCounts();
  const scrollRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);

  // Пока колонку тянут, ширина живёт здесь: писать её в настройки на каждый пиксель значило бы
  // перерисовывать всю страницу и слать запрос за запросом.
  const [draft, setDraft] = useState<{ column: TableColumn; width: number } | null>(null);
  const [dragging, setDragging] = useState<TableColumn | null>(null);
  const [dropTarget, setDropTarget] = useState<TableColumn | null>(null);
  /**
   * Задачи, отправленные в план прямо сейчас. Ответ и перечитывание плана занимают доли секунды,
   * но щелчок обязан отзываться сразу — иначе кажется, что он не сработал, и его повторяют.
   */
  const [addingKeys, setAddingKeys] = useState<ReadonlySet<string>>(() => new Set());

  /**
   * Свёрнутые родители. По умолчанию подзадачи раскрыты: таблица — список того, что в работе,
   * и подзадача, назначенная на человека, не должна прятаться за чужой строкой. Свернуть можно
   * любую задачу, выбор живёт до перезагрузки страницы.
   */
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(() => new Set());

  /**
   * Задача, под которой открыта строка создания подзадачи — «+» в строке, как в списках Jira.
   * Одна на таблицу: две открытые строки ввода спорили бы за фокус и за Enter.
   */
  const [creatingUnder, setCreatingUnder] = useState<string | null>(null);

  const visible = useMemo(() => {
    const list: VisibleRow[] = [];
    for (const row of issues) {
      list.push({ issue: row, row, parentKey: null });
      if (collapsed.has(row.key)) continue;
      for (const child of row.children) {
        list.push({ issue: child, row: null, parentKey: row.key });
      }
    }
    return list;
  }, [collapsed, issues]);

  /**
   * Строка создания встаёт под последней видимой подзадачей своей задачи: новая подзадача
   * появится там же, и глазу не придётся её искать.
   */
  const createAfterKey = useMemo(() => {
    if (creatingUnder === null) return null;
    let last: string | null = null;
    for (const item of visible) {
      if (item.issue.key === creatingUnder) last = item.issue.key;
      else if (last !== null && item.parentKey === creatingUnder) last = item.issue.key;
      else if (last !== null) break;
    }
    return last;
  }, [creatingUnder, visible]);

  /** Открыть строку создания: свёрнутая задача раскрывается, иначе строку не видно под чем. */
  const startCreate = useCallback((row: IssueRow): void => {
    setCollapsed((previous) => {
      if (!previous.has(row.key)) return previous;
      const next = new Set(previous);
      next.delete(row.key);
      return next;
    });
    setCreatingUnder(row.key);
  }, []);

  /**
   * Место под стрелку раскрытия держат все строки, но только когда раскрывать есть что: иначе
   * у раздела без подзадач названия сдвинулись бы ради пустого столбца.
   */
  const hasTree = useMemo(() => issues.some((row) => row.children.length > 0), [issues]);

  /**
   * Свернуть или раскрыть подзадачи. Выбранная подзадача при сворачивании передаёт выбор
   * родителю — иначе выбор остался бы на строке, которой не видно, и Enter открыл бы невидимое.
   */
  const toggleRow = useCallback(
    (row: IssueRow): void => {
      const closing = !collapsed.has(row.key);
      setCollapsed((previous) => {
        const next = new Set(previous);
        if (closing) next.add(row.key);
        else next.delete(row.key);
        return next;
      });
      if (closing && row.children.some((child) => child.key === selectedKey)) onSelect(row.key);
    },
    [collapsed, onSelect, selectedKey],
  );

  /** Уже в плане — с точки зрения таблицы это и подтверждённая строка, и только что добавленная. */
  const inPlan = useCallback(
    (issueKey: string): boolean => planKeys.has(issueKey) || addingKeys.has(issueKey),
    [addingKeys, planKeys],
  );

  /**
   * Взять задачу в план. Отметка ставится до ответа сервера и снимается, если он отказал:
   * добавление в план — действие мелкое и частое, ждать от него подтверждения незачем.
   */
  const addToPlan = useCallback(
    (issue: IssueSummary): void => {
      if (inPlan(issue.key)) return;

      setAddingKeys((previous) => new Set(previous).add(issue.key));

      void Promise.resolve(onAddToPlan(issue)).catch(() => {
        setAddingKeys((previous) => {
          const next = new Set(previous);
          next.delete(issue.key);
          return next;
        });
      });
    },
    [inPlan, onAddToPlan],
  );

  const widthOf = useCallback(
    (column: TableColumn): number =>
      draft?.column === column ? draft.width : (widths[column] ?? DEFAULT_COLUMN_WIDTHS[column]),
    [draft, widths],
  );

  /**
   * Место колонки плана в пользовательском порядке: сразу за названием. Названия в виде может
   * и не быть — тогда отметка встаёт первой, как раньше, и строка не остаётся без неё.
   */
  const planIndex = Math.max(0, columns.indexOf('summary') + 1);

  const layout = useMemo(() => {
    const tracks: string[] = [];
    const fitTracks: string[] = [];
    let total = ROW_PADDING + PLAN_COLUMN_WIDTH;

    for (const column of columns) {
      const width = widthOf(column);
      total += width;
      // Название забирает свободный остаток, заданная ширина работает как его минимум.
      tracks.push(column === 'summary' ? `minmax(${width}px, 1fr)` : `${width}px`);
      fitTracks.push(
        column === 'summary' ? `minmax(${SUMMARY_MIN_WIDTH}px, 1fr)` : `minmax(0, ${width}px)`,
      );
    }

    tracks.splice(planIndex, 0, `${PLAN_COLUMN_WIDTH}px`);
    fitTracks.splice(planIndex, 0, `${PLAN_COLUMN_WIDTH}px`);

    // Ширину шапки и строк задаём одним числом. Положиться на max-content нельзя: у шапки
    // это длина заголовков, у строк — длина самого длинного названия, и они бы разъехались.
    return { grid: tracks.join(' '), fit: fitTracks.join(' '), width: `${total}px` };
  }, [columns, planIndex, widthOf]);

  // Бесконечная прокрутка: следующая страница подгружается, когда конец списка входит в вид.
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) onLoadMore();
      },
      { root: scrollRef.current, rootMargin: '200px' },
    );

    observer.observe(sentinel);
    return () => {
      observer.disconnect();
    };
  }, [hasMore, onLoadMore, issues.length]);

  const selectRow = useCallback(
    (issueKey: string): void => {
      onSelect(issueKey);
      bodyRef.current?.querySelector<HTMLElement>(`[data-key="${issueKey}"]`)?.scrollIntoView({
        block: 'nearest',
      });
    },
    [onSelect],
  );

  /**
   * Стрелки ходят по строкам, Enter открывает, P берёт задачу в план — ровно то, что обещает
   * подсказка в подвале. Влево и вправо — по дереву, как в любом списке с вложенностью:
   * вправо раскрывает подзадачи, влево сворачивает их или возвращает к родителю.
   *
   * Слушаем окно, а не тело таблицы: с обработчиком на элементе клавиши молчали, пока по
   * таблице не щёлкнут, — а подсказка висит на экране с первой секунды. Набор в поле ввода
   * и открытое модальное окно клавиши не перехватывают.
   */
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (visible.length === 0) return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      // Открыто любое окно — от карточки задачи до подтверждения удаления — клавиши не наши.
      if (isTypingTarget(event.target)) return;
      if (document.querySelector('[role="dialog"], [role="alertdialog"]')) return;

      const index = visible.findIndex((item) => item.issue.key === selectedKey);

      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        const delta = event.key === 'ArrowDown' ? 1 : -1;
        const next = index === -1 ? 0 : Math.min(Math.max(index + delta, 0), visible.length - 1);
        const target = visible[next];
        if (target) selectRow(target.issue.key);
        return;
      }

      if (index < 0) return;
      const current = visible[index];
      if (!current) return;
      const issue = current.issue;

      if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
        const row = current.row;

        if (row === null) {
          // Подзадача: влево — к родителю, вправо идти некуда.
          if (event.key === 'ArrowLeft' && current.parentKey) {
            event.preventDefault();
            selectRow(current.parentKey);
          }
          return;
        }

        if (row.children.length === 0) return;
        event.preventDefault();

        const open = !collapsed.has(row.key);
        if (event.key === 'ArrowRight') {
          // Раскрытая уже — шаг к первой подзадаче, как в дереве файлов.
          const first = row.children[0];
          if (!open) toggleRow(row);
          else if (first) selectRow(first.key);
        } else if (open) {
          toggleRow(row);
        }
        return;
      }

      if (event.key === 'Enter') {
        event.preventDefault();
        onOpen(issue);
        return;
      }

      // Раскладка клавиатуры своя у каждого: латинская «p» и русская «з» — одна и та же клавиша.
      if (event.key === 'p' || event.key === 'P' || event.key === 'з' || event.key === 'З') {
        event.preventDefault();
        addToPlan(issue);
      }
    },
    [addToPlan, collapsed, onOpen, selectRow, selectedKey, toggleRow, visible],
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  const finishDrag = useCallback(() => {
    setDragging(null);
    setDropTarget(null);
  }, []);

  return (
    <div
      className={styles.wrap}
      ref={scrollRef}
      // Строки с подзадачами раскрываются — для скринридера это дерево-таблица, а не таблица.
      role="treegrid"
      aria-rowcount={visible.length}
      aria-colcount={columns.length}
      style={
        {
          '--table-grid': layout.grid,
          '--table-grid-fit': layout.fit,
          '--table-width': layout.width,
        } as React.CSSProperties
      }
    >
      <div className={styles.head} role="row">
        {withPlanColumn(
          columns.map((column) => (
            <ColumnHeader
              key={column}
              column={column}
              width={widthOf(column)}
              sort={sort}
              dir={dir}
              onSort={onSort}
              onResize={onResizeColumn}
              onResizeDraft={setDraft}
              dragging={dragging === column}
              dropTarget={dropTarget === column && dragging !== null && dragging !== column}
              onDragStart={() => setDragging(column)}
              onDragEnter={() => setDropTarget(column)}
              onDrop={() => {
                if (dragging && dragging !== column) onMoveColumn(dragging, column);
                finishDrag();
              }}
              onDragEnd={finishDrag}
            />
          )),
          planIndex,
          // Заголовка у колонки плана нет: иконка в строках говорит сама за себя.
          <div
            key="plan"
            className={styles.planHead}
            role="columnheader"
            aria-label={t('table.inPlan')}
          />,
        )}
      </div>

      <div
        className={styles.body}
        ref={bodyRef}
        tabIndex={0}
        role="rowgroup"
        aria-label={t('table.columns.summary')}
      >
        {visible.map(({ issue, row }) => (
          <Fragment key={issue.key}>
            <div
              data-key={issue.key}
              role="row"
              tabIndex={-1}
              className={`${styles.row} ${row === null ? styles.rowChild : ''} ${
                inPlan(issue.key) ? styles.rowInPlan : ''
              } ${issue.key === selectedKey ? styles.rowSelected : ''}`}
              onClick={() => {
                onSelect(issue.key);
                onOpen(issue);
              }}
              aria-selected={issue.key === selectedKey}
              aria-level={row === null ? 2 : 1}
              {...(row !== null && row.children.length > 0
                ? { 'aria-expanded': !collapsed.has(row.key) }
                : {})}
            >
              <span className={styles.selectionBar} aria-hidden="true" />

              {withPlanColumn(
                columns.map((column) => (
                  <div
                    key={column}
                    role="cell"
                    data-column={column}
                    className={cellClassName(column, issue)}
                    {...(column === 'summary' ? { title: summaryTitle(issue) } : {})}
                  >
                    {column === 'summary' ? (
                      <SummaryCell
                        issue={issue}
                        row={row}
                        hasTree={hasTree}
                        expanded={row !== null && !collapsed.has(row.key)}
                        onToggle={toggleRow}
                        onAddSubtask={row !== null && canHaveSubtasks(issue) ? startCreate : undefined}
                        unread={unreadComments.get(issue.key) ?? 0}
                      />
                    ) : (
                      cellContent(column, issue, t, sectionColor)
                    )}
                  </div>
                )),
                planIndex,
                <div key="plan" className={styles.planCell} role="cell">
                  {inPlan(issue.key) ? (
                    <span className={styles.planMark} title={t('table.inPlan')}>
                      <PlanMarkIcon />
                      <span className="visually-hidden">{t('table.inPlan')}</span>
                    </span>
                  ) : (
                    <button
                      type="button"
                      className={styles.planAdd}
                      title={t('table.addToPlan')}
                      aria-label={t('table.addToPlan')}
                      onClick={(event) => {
                        // Щелчок по строке открывает задачу — здесь он не должен всплывать.
                        event.stopPropagation();
                        addToPlan(issue);
                      }}
                    >
                      <PlanAddIcon />
                    </button>
                  )}
                </div>,
              )}
            </div>
            {issue.key === createAfterKey && (
              <SubtaskCreateRow
                parent={issues.find((item) => item.key === creatingUnder) ?? null}
                onClose={() => setCreatingUnder(null)}
              />
            )}
          </Fragment>
        ))}

        <div ref={sentinelRef} className={styles.sentinel}>
          {loadingMore && <span className={styles.loadingMore}>{t('table.loadingMore')}</span>}
        </div>
      </div>
    </div>
  );
}

/**
 * Колонка плана вставляется в готовый ряд ячеек, а не рисуется вместе с остальными: своей
 * записи в настройках вида у неё нет, и в `columns` её не найти — есть только позиция.
 */
function withPlanColumn(
  cells: React.ReactNode[],
  index: number,
  planCell: React.ReactNode,
): React.ReactNode[] {
  const result = [...cells];
  result.splice(index, 0, planCell);
  return result;
}

/**
 * Непрочитанные комментарии задачи. Считаются только «мои» задачи — те же, что попадают
 * в колокольчик: исполнитель или автор. Щелчок по строке открывает карточку и метку гасит.
 */
function UnreadCommentsBadge({ count, t }: { count: number; t: TFunction }): React.ReactElement {
  const label = t('table.unreadComments', { count });

  return (
    <span className={styles.unread} title={label}>
      <CommentIcon />
      <span aria-hidden="true">{count}</span>
      <span className="visually-hidden">{label}</span>
    </span>
  );
}

/** Реплика: та же линия в 1.2, что у остальных значков таблицы. */
function CommentIcon(): React.ReactElement {
  return (
    <svg viewBox="0 0 16 16" width="11" height="11" aria-hidden="true" focusable="false">
      <path
        d="M2.6 4.2a1.2 1.2 0 0 1 1.2-1.2h8.4a1.2 1.2 0 0 1 1.2 1.2v5a1.2 1.2 0 0 1-1.2 1.2H6.4L3.4 13V10.4a1.2 1.2 0 0 1-.8-1.2Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** Отметка «в плане» — та же тройка отрезков, что у «Планирования» в боковой колонке. */
function PlanMarkIcon(): React.ReactElement {
  return (
    <svg viewBox="0 0 16 16" width="13" height="13" aria-hidden="true" focusable="false">
      <path
        d="M3 4.5h6M6 8h7M3 11.5h5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </svg>
  );
}

/** Кнопка «взять в план»: плюс в тон отметке, которая встанет на его место. */
function PlanAddIcon(): React.ReactElement {
  return (
    <svg viewBox="0 0 16 16" width="13" height="13" aria-hidden="true" focusable="false">
      <path
        d="M8 3.5v9M3.5 8h9"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </svg>
  );
}

/** Пока набирают текст, клавиши таблицы молчат: «p» в поиске — буква, а не команда. */
function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target.isContentEditable;
}

/** Раздел — единственная колонка без сортировки: порядок по нему повторил бы порядок по ключу. */
function sortFieldOf(column: TableColumn): IssueSortField | null {
  return column === 'section' ? null : column;
}

function ColumnHeader({
  column,
  width,
  sort,
  dir,
  onSort,
  onResize,
  onResizeDraft,
  dragging,
  dropTarget,
  onDragStart,
  onDragEnter,
  onDrop,
  onDragEnd,
}: {
  column: TableColumn;
  width: number;
  sort: IssueSortField;
  dir: SortDirection;
  onSort: (field: IssueSortField) => void;
  onResize: (column: TableColumn, width: number) => void;
  onResizeDraft: (draft: { column: TableColumn; width: number } | null) => void;
  dragging: boolean;
  dropTarget: boolean;
  onDragStart: () => void;
  onDragEnter: () => void;
  onDrop: () => void;
  onDragEnd: () => void;
}): React.ReactElement {
  const { t } = useTranslation();
  const field = sortFieldOf(column);
  const active = field !== null && sort === field;
  const label = t(`table.columns.${column}`);

  // Точка отсчёта протягивания. Считать от текущей ширины нельзя: она меняется по ходу движения.
  const origin = useRef<{ x: number; width: number } | null>(null);

  const widthAt = (clientX: number): number => {
    const start = origin.current;
    if (!start) return width;
    return Math.min(COLUMN_MAX_WIDTH, Math.max(COLUMN_MIN_WIDTH, start.width + clientX - start.x));
  };

  return (
    <div
      className={`${styles.headCell} ${dragging ? styles.headDragging : ''} ${
        dropTarget ? styles.headDropTarget : ''
      }`}
      role="columnheader"
      aria-sort={active ? (dir === 'asc' ? 'ascending' : 'descending') : 'none'}
      draggable
      onDragStart={(event) => {
        // Firefox начинает перенос только при заполненном dataTransfer.
        event.dataTransfer.setData('text/plain', column);
        event.dataTransfer.effectAllowed = 'move';
        onDragStart();
      }}
      onDragOver={(event) => {
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';
      }}
      onDragEnter={onDragEnter}
      onDrop={(event) => {
        event.preventDefault();
        onDrop();
      }}
      onDragEnd={onDragEnd}
    >
      {field === null ? (
        <span className={`${styles.headLabel} truncate`}>{label}</span>
      ) : (
        <button
          type="button"
          className={`${styles.headButton} ${active ? styles.headActive : ''}`}
          onClick={() => onSort(field)}
          title={active ? t(dir === 'asc' ? 'table.sortAscending' : 'table.sortDescending') : label}
        >
          <span className="truncate">{label}</span>
          {/* Стрелка направления в макете не нарисована — без неё непонятно, как отсортировано. */}
          {active && (
            <span className={styles.sortArrow} aria-hidden="true">
              {dir === 'asc' ? '↑' : '↓'}
            </span>
          )}
        </button>
      )}

      <div
        className={styles.resizer}
        role="separator"
        aria-orientation="vertical"
        aria-label={t('table.resizeColumn', { name: label })}
        tabIndex={0}
        title={t('table.resizeColumnHint')}
        onPointerDown={(event) => {
          event.preventDefault();
          event.stopPropagation();
          origin.current = { x: event.clientX, width };
          onResizeDraft({ column, width });
          event.currentTarget.setPointerCapture(event.pointerId);
        }}
        onPointerMove={(event) => {
          if (!origin.current) return;
          onResizeDraft({ column, width: widthAt(event.clientX) });
        }}
        onPointerUp={(event) => {
          const start = origin.current;
          if (!start) return;
          const next = widthAt(event.clientX);
          origin.current = null;
          onResizeDraft(null);
          if (next !== start.width) onResize(column, next);
        }}
        onPointerCancel={() => {
          origin.current = null;
          onResizeDraft(null);
        }}
        // Двойной щелчок по границе возвращает ширину из макета — привычный жест таблиц.
        onDoubleClick={() => onResize(column, DEFAULT_COLUMN_WIDTHS[column])}
        onKeyDown={(event) => {
          if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
          event.preventDefault();
          const delta = event.key === 'ArrowRight' ? RESIZE_STEP : -RESIZE_STEP;
          onResize(
            column,
            Math.min(COLUMN_MAX_WIDTH, Math.max(COLUMN_MIN_WIDTH, width + delta)),
          );
        }}
      />
    </div>
  );
}

function cellClassName(column: TableColumn, issue: IssueSummary): string {
  switch (column) {
    case 'section':
      return styles.cellSection ?? '';
    case 'key':
      return `mono ${styles.cellKey}`;
    case 'summary':
      return styles.cellSummary ?? '';
    case 'status':
      return styles.cellStatus ?? '';
    case 'assignee':
      return styles.cellAssignee ?? '';
    case 'updated':
      return `mono ${styles.cellMuted}`;
    case 'duedate':
      return `mono ${styles.cellMuted} ${isOverdue(issue.duedate) ? styles.overdue : ''}`;
    default:
      return '';
  }
}

/**
 * Подзадача, чей родитель не прошёл отбор, стоит отдельной строкой — с его ключом впереди,
 * как в списках Jira. Задача эпика так не помечается: эпик в таблице не родитель, а тема.
 */
function standsAlone(issue: IssueSummary, row: IssueRow | null): boolean {
  return row !== null && issue.type?.subtask === true && issue.parent !== null;
}

/** Подсказка названия: у одинокой подзадачи в ней и родитель — ключ сам по себе мало что говорит. */
function summaryTitle(issue: IssueSummary): string {
  if (issue.type?.subtask === true && issue.parent) {
    return `${issue.parent.key} ${issue.parent.summary} / ${issue.summary}`;
  }
  return issue.summary;
}

/**
 * Ячейка названия. Всё служебное стоит до текста, кроме счётчика подзадач: название обрезается
 * многоточием, и метка за ним пропадала бы ровно у длинных названий. Счётчик прижат к краю
 * ячейки отдельным элементом, поэтому обрезка его не задевает.
 */
function SummaryCell({
  issue,
  row,
  hasTree,
  expanded,
  onToggle,
  onAddSubtask,
  unread,
}: {
  issue: IssueSummary;
  row: IssueRow | null;
  hasTree: boolean;
  expanded: boolean;
  onToggle: (row: IssueRow) => void;
  /** Задаче можно завести подзадачу прямо из строки; у подзадачи и эпика кнопки нет. */
  onAddSubtask: ((row: IssueRow) => void) | undefined;
  unread: number;
}): React.ReactElement {
  const { t } = useTranslation();

  return (
    <>
      {hasTree && row !== null && row.children.length > 0 && (
        <button
          type="button"
          className={styles.treeToggle}
          aria-expanded={expanded}
          aria-label={t(expanded ? 'table.collapse' : 'table.expand', { key: issue.key })}
          title={t(expanded ? 'table.collapse' : 'table.expand', { key: issue.key })}
          onClick={(event) => {
            // Щелчок по строке открывает задачу — стрелка только сворачивает.
            event.stopPropagation();
            onToggle(row);
          }}
        >
          <ChevronIcon open={expanded} />
        </button>
      )}
      {hasTree && row !== null && row.children.length === 0 && (
        <span className={styles.treeSpacer} aria-hidden="true" />
      )}
      {/* Подзадача сдвинута на шаг вглубь — под стрелку своего родителя и ещё на одну. */}
      {row === null && <span className={styles.treeIndent} aria-hidden="true" />}

      {unread > 0 && <UnreadCommentsBadge count={unread} t={t} />}
      <span className={`${styles.summaryText} truncate`}>
        {standsAlone(issue, row) && issue.parent && <ParentPrefix parent={issue.parent} />}
        {issue.summary}
      </span>
      {row?.subtaskProgress && <SubtaskCounter progress={row.subtaskProgress} />}
      {row !== null && onAddSubtask && (
        <button
          type="button"
          className={styles.addSubtask}
          title={t('table.addSubtask', { key: issue.key })}
          aria-label={t('table.addSubtask', { key: issue.key })}
          onClick={(event) => {
            // Щелчок по строке открывает задачу — здесь он открывает только строку создания.
            event.stopPropagation();
            onAddSubtask(row);
          }}
        >
          <AddSubtaskIcon />
        </button>
      )}
    </>
  );
}

/** Подзадачу заводят у обычной задачи: у подзадачи детей не бывает, у эпика дети — задачи. */
function canHaveSubtasks(issue: IssueSummary): boolean {
  return issue.type !== null && issue.type.hierarchyLevel === 0;
}

/**
 * Строка создания подзадачи под задачей — как «+» в списке Jira: название и Enter. Тип
 * подставляется сам, если в разделе он один; строка не закрывается после создания, потому что
 * подзадачи заводят пачкой. Остальные поля правятся в карточке созданной.
 */
function SubtaskCreateRow({
  parent,
  onClose,
}: {
  parent: IssueRow | null;
  onClose: () => void;
}): React.ReactElement | null {
  const { t } = useTranslation();
  const options = useQuery(createOptionsQuery(parent?.projectId ?? null, null));
  const create = useIssueCreate();
  const inputRef = useRef<HTMLInputElement>(null);

  const [summary, setSummary] = useState('');
  const [typeId, setTypeId] = useState('');

  if (!parent) return null;

  const types = (options.data?.issueTypes ?? []).filter((type) => type.subtask);
  const chosen = typeId || types[0]?.id || '';
  const error = create.error instanceof ApiRequestError ? create.error : null;
  const parentKey = parent.key;
  const projectId = parent.projectId;

  function submit(): void {
    const text = summary.trim();
    if (text === '' || chosen === '' || create.isPending) return;
    create.mutate(
      { projectId, issueTypeId: chosen, parentKey, summary: text },
      {
        onSuccess: () => {
          setSummary('');
          inputRef.current?.focus();
        },
      },
    );
  }

  return (
    <div className={styles.createRow} role="row" aria-level={2}>
      <div className={styles.createCell} role="cell">
        <input
          ref={inputRef}
          className={styles.createInput}
          value={summary}
          onChange={(event) => setSummary(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              submit();
            } else if (event.key === 'Escape') {
              event.preventDefault();
              onClose();
            }
          }}
          placeholder={t('table.subtaskPlaceholder', { key: parentKey })}
          aria-label={t('table.subtaskPlaceholder', { key: parentKey })}
          maxLength={255}
          autoFocus
        />
        {types.length > 1 && (
          <select
            className={styles.createSelect}
            value={chosen}
            onChange={(event) => setTypeId(event.target.value)}
            aria-label={t('table.columns.type')}
          >
            {types.map((type) => (
              <option value={type.id} key={type.id}>
                {type.name}
              </option>
            ))}
          </select>
        )}
        <button
          type="button"
          className={styles.createSubmit}
          onClick={submit}
          disabled={create.isPending || summary.trim() === '' || chosen === ''}
        >
          {create.isPending ? t('create.creating') : t('create.submit')}
        </button>
        <button type="button" className={styles.createCancel} onClick={onClose}>
          {t('issue.cancel')}
        </button>

        {options.isSuccess && types.length === 0 && (
          <span className={styles.createNote}>{t('table.noSubtaskTypes')}</span>
        )}
        {error && (
          <span className={styles.createError} role="alert">
            {error.code === 'JIRA_REQUIRED_FIELDS'
              ? t('create.requiredBody', { fields: String(error.params?.['fields'] ?? '') })
              : t(`errors.${error.code}` satisfies `errors.${ErrorCode}`)}
          </span>
        )}
      </div>
    </div>
  );
}

/**
 * Счётчик подзадач: готово из скольких. Считает все подзадачи, в том числе скрытые
 * отбором, — поэтому под родителем с «2/3» может стоять одна строка, и счётчик объясняет почему.
 */
function SubtaskCounter({ progress }: { progress: SubtaskProgress }): React.ReactElement {
  const { t } = useTranslation();
  const label = t('table.subtaskProgress', { done: progress.done, total: progress.total });
  const complete = progress.done === progress.total;

  return (
    <span
      className={`mono ${styles.subtaskCounter} ${complete ? styles.subtaskCounterDone : ''}`}
      title={label}
    >
      <SubtasksIcon />
      <span aria-hidden="true">
        {progress.done}/{progress.total}
      </span>
      <span className="visually-hidden">{label}</span>
    </span>
  );
}

/**
 * «Добавить подзадачу»: ветка с плюсом на конце. Не голый плюс — он уже занят соседней колонкой
 * «взять в план», и два одинаковых значка рядом читались бы как одно действие.
 */
function AddSubtaskIcon(): React.ReactElement {
  return (
    <svg viewBox="0 0 16 16" width="13" height="13" aria-hidden="true" focusable="false">
      <path
        d="M3.5 2.5v6A1.5 1.5 0 0 0 5 10h3.5M12 7.5v5M9.5 10h5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

/** Стрелка раскрытия: вправо — свёрнуто, вниз — подзадачи видны. */
function ChevronIcon({ open }: { open: boolean }): React.ReactElement {
  return (
    <svg
      viewBox="0 0 16 16"
      width="12"
      height="12"
      aria-hidden="true"
      focusable="false"
      className={open ? styles.chevronOpen : undefined}
    >
      <path
        d="M6 4l4 4-4 4"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** Значок подзадач: ветка от родителя к двум детям, в той же линии, что остальные значки. */
function SubtasksIcon(): React.ReactElement {
  return (
    <svg viewBox="0 0 16 16" width="11" height="11" aria-hidden="true" focusable="false">
      <path
        d="M4 3v7.5a1.5 1.5 0 0 0 1.5 1.5H9M4 6.5h5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinecap="round"
      />
      <circle cx="11" cy="6.5" r="1.6" fill="none" stroke="currentColor" strokeWidth="1.3" />
      <circle cx="11" cy="12" r="1.6" fill="none" stroke="currentColor" strokeWidth="1.3" />
    </svg>
  );
}

function cellContent(
  column: TableColumn,
  issue: IssueSummary,
  t: TFunction,
  sectionColor: (projectKey: string) => number | null,
): React.ReactNode {
  switch (column) {
    case 'section':
      return (
        <>
          <SectionMark colorIndex={sectionColor(issue.projectKey)} />
          <span className={`${styles.sectionName} truncate`}>{issue.projectName}</span>
        </>
      );
    case 'key':
      return issue.key;
    case 'type':
      return <TypeBadge type={issue.type} />;
    // Три колонки правятся прямо в строке: значение открывает список допустимых.
    case 'status':
      return <StatusCell issue={issue} />;
    case 'assignee':
      return <AssigneeCell issue={issue} />;
    case 'priority':
      return <PriorityCell issue={issue} />;
    case 'updated':
      return formatUpdated(issue.updated, t);
    case 'duedate':
      return issue.duedate ? formatShortDate(issue.duedate) : t('table.noDue');
    default:
      return null;
  }
}
